/**
 * @format
 */

import * as types from './actions';
import { TableColumnPreference, Task } from './types';

// Define TypeScript Interfaces
// interface User {
//   id: string;
//   first_name: string;
//   last_name: string;
//   fullname: string;
//   email_primary: string;
//   gravatar: string;
//   profile_pic: string;
//   nameOrEmail: string;
//   // Add other fields from the detailed user object if needed
// }

// interface Assignee {
//   id: string;
//   user: User;
// }

// interface Creator {
//   user: User; // The detailed user object
// }

// interface Task {
//   id: string;
//   title: string;
//   description: string;
//   priority: string | null;
//   status: string;
//   position_id: number;
//   organization_id: string;
//   due_date: string;
//   parent_id: string | null;
//   created_at: string;
//   updated_at: string;
//   recurring_task_id: string | null;
//   recurring_index: number;
//   sub_tasks: Task[]; // Sub-tasks are also Task objects
//   attachments: any[]; // Array of attachment objects (structure not detailed, assume any)
//   recurringTask: Task | null;
//   resource_type: string;
//   resource_id: string;
//   assignees: Assignee[];
//   creator: Creator;
//   // Add other fields from the detailed task object if needed
// }

// interface TableColumnPreference {
//   field: string;
//   visible: boolean;
//   order: number;
//   // Add other properties if needed for column preferences
// }

interface TaskDashboardState {
  dashboardTasksLoading: boolean;
  dashboardTasksError: any;
  dashboardTasksById: { [key: string]: Task };
  dashboardTaskOrder: Record<string, string[]>; // New: Map to maintain the order of task IDs per status
  isAllTaskSelected: Record<string, boolean>; // New: State to manage select all checkbox per status

  addDashboardTaskLoading: boolean;
  addDashboardTaskError: any;

  updateDashboardTaskLoading: boolean;
  updateDashboardTaskError: any;
  originalTaskBeforeUpdate: Task | null;

  deleteDashboardTaskLoading: boolean;
  deleteDashboardTaskError: any;
  originalTaskBeforeDelete: Task | null;

  getDashboardTaskByIdLoading: boolean;
  getDashboardTaskByIdError: any;

  loadTableColumnPreferencesLoading: boolean;
  loadTableColumnPreferencesError: any;
  updateTableColumnPreferencesLoading: boolean;
  updateTableColumnPreferencesError: any;
  tableColumnPreferences: TableColumnPreference[];

  dashboardViewMode: 'list' | 'kanban';
}

interface Action {
  type: string;
  payload?: any; // Payload type will vary based on action
}

const initialState: TaskDashboardState = {
  // State for loading all tasks for the dashboard
  dashboardTasksLoading: false,
  dashboardTasksError: null,
  dashboardTasksById: {}, // Normalized tasks by ID for the dashboard
  dashboardTaskOrder: {
    todo: [],
    in_progress: [],
    complete: [],
  }, // Initialize the order map

  // State for adding a task
  addDashboardTaskLoading: false,
  addDashboardTaskError: null,

  // State for updating a task
  updateDashboardTaskLoading: false,
  updateDashboardTaskError: null,
  originalTaskBeforeUpdate: null,

  // State for deleting a task
  deleteDashboardTaskLoading: false,
  deleteDashboardTaskError: null,
  originalTaskBeforeDelete: null,

  // State for loading a single task by ID
  getDashboardTaskByIdLoading: false,
  getDashboardTaskByIdError: null,

  // State for table column preferences
  loadTableColumnPreferencesLoading: false,
  loadTableColumnPreferencesError: null,
  updateTableColumnPreferencesLoading: false,
  updateTableColumnPreferencesError: null,
  tableColumnPreferences: [], // Array to store column preferences (e.g., { field: 'title', visible: true, order: 1 })

  // State for view mode (list/kanban)
  dashboardViewMode: 'list', // Default to 'list'
  isAllTaskSelected: {
    todo: false,
    in_progress: false,
    complete: false,
  }, // Initialize as a hash map
};

export default (
  state: TaskDashboardState = initialState,
  { type, payload }: Action
): TaskDashboardState => {
  switch (type) {
    // Load All Tasks for Dashboard
    case types.GET_DASHBOARD_TASKS_LOADING:
      return {
        ...state,
        dashboardTasksLoading: true,
        dashboardTasksError: null,
      };
    case types.GET_DASHBOARD_TASKS_SUCCESS: {
      const { tasks, filters, page } = payload as {
        tasks: Task[];
        filters: {
          AND: {
            filterKey: string;
            filterOperator: string;
            filterValue: string;
          }[];
        };
        page: number;
      };

      // Determine the status from filters
      const statusFilter = filters.AND.find(
        filter => filter.filterKey === 'status'
      );
      const status = statusFilter ? statusFilter.filterValue : 'unknown'; // Default to 'unknown' or handle error

      // Merge new tasks with existing ones, overwriting if IDs match
      const newTasksById = tasks.reduce((acc, task) => {
        acc[task.id] = {
          ...task,
          isSelected: state.isAllTaskSelected[task.status] || false,
        };
        return acc;
      }, {} as { [key: string]: Task });

      // Update the order of task IDs for the specific status
      const newTaskOrderForStatus = tasks.map(task => task.id);

      let updatedDashboardTaskOrder = { ...state.dashboardTaskOrder };

      if (page === 1) {
        // If it's the first page, replace the order for this status
        updatedDashboardTaskOrder[status] = newTaskOrderForStatus;
      } else {
        // If it's a subsequent page, append to the existing order for this status
        updatedDashboardTaskOrder[status] = [
          ...(updatedDashboardTaskOrder[status] || []),
          ...newTaskOrderForStatus,
        ];
      }

      return {
        ...state,
        dashboardTasksLoading: false,
        dashboardTasksError: null,
        dashboardTasksById: { ...state.dashboardTasksById, ...newTasksById },
        dashboardTaskOrder: updatedDashboardTaskOrder,
      };
    }
    case types.GET_DASHBOARD_TASKS_FAILURE:
      return {
        ...state,
        dashboardTasksLoading: false,
        dashboardTasksError: payload,
      };

    // Add Task
    case types.ADD_DASHBOARD_TASK_LOADING:
      return {
        ...state,
        addDashboardTaskLoading: true,
        addDashboardTaskError: null,
      };
    case types.ADD_DASHBOARD_TASK_SUCCESS: {
      // Optimistic update: Add the new task to the state
      const addedTask = payload.data as Task;
      const updatedTask = {
        ...addedTask,
        isSelected: state.isAllTaskSelected[addedTask.status] || false,
        // Populate resource_name based on resource_type if available
        resource_name: payload.resource_name,
        // fake roles since api not sending _roles, will get updated on loadAllTask
        _roles: ['workspaceMember', 'workspaceAdmin'],
      };

      const updatedDashboardTasksById = { ...state.dashboardTasksById };
      const updatedDashboardTaskOrder = { ...state.dashboardTaskOrder };

      if (addedTask.parent_id) {
        // If it's a subtask, find the parent task and add it to its sub_tasks array
        const parentTask = updatedDashboardTasksById[addedTask.parent_id];
        if (parentTask) {
          const updatedSubTasks = [
            updatedTask,
            ...(parentTask.sub_tasks || []),
          ];
          updatedDashboardTasksById[addedTask.parent_id] = {
            ...parentTask,
            sub_tasks: updatedSubTasks,
          };
        }
      } else {
        // If it's a top-level task, add it to dashboardTasksById and dashboardTaskOrder
        updatedDashboardTasksById[updatedTask.id] = updatedTask;
        if (updatedTask.status) {
          updatedDashboardTaskOrder[updatedTask.status] = [
            updatedTask.id,
            ...(updatedDashboardTaskOrder[updatedTask.status] || []),
          ];
        }
      }

      return {
        ...state,
        addDashboardTaskLoading: false,
        addDashboardTaskError: null,
        dashboardTasksById: updatedDashboardTasksById,
        dashboardTaskOrder: updatedDashboardTaskOrder,
      };
    }
    case types.ADD_DASHBOARD_TASK_FAILURE: {
      // Revert optimistic update: Remove the task that failed to be added
      const failedAddedTaskId = payload?.taskId; // Assuming payload contains taskId of the failed task
      const tasksAfterAddFailure = { ...state.dashboardTasksById };
      const updatedDashboardTaskOrder = { ...state.dashboardTaskOrder };

      if (failedAddedTaskId) {
        const failedTask = state.dashboardTasksById[failedAddedTaskId];
        if (failedTask && failedTask.status) {
          updatedDashboardTaskOrder[failedTask.status] = (
            updatedDashboardTaskOrder[failedTask.status] || []
          ).filter(id => id !== failedAddedTaskId);
        }
        delete tasksAfterAddFailure[failedAddedTaskId];
      }
      return {
        ...state,
        addDashboardTaskLoading: false,
        addDashboardTaskError: payload,
        dashboardTasksById: tasksAfterAddFailure,
        dashboardTaskOrder: updatedDashboardTaskOrder,
      };
    }

    // Update Task
    case types.UPDATE_DASHBOARD_TASK_LOADING: {
      const taskIdToUpdate = payload?.taskId;
      const updatedAttributes = payload.updatedAttributes;
      const originalTask = payload.originalTask;
      const newStatus = updatedAttributes.status;

      const newDashboardTasksById = { ...state.dashboardTasksById };
      const newDashboardTaskOrder = { ...state.dashboardTaskOrder };

      if (originalTask?.parent_id) {
        // Handle subtask update
        const parentTask = newDashboardTasksById[originalTask.parent_id];
        if (parentTask) {
          const updatedSubTasks = parentTask.sub_tasks.map((subTask: Task) =>
            subTask.id === taskIdToUpdate
              ? { ...subTask, ...updatedAttributes }
              : subTask
          );
          newDashboardTasksById[originalTask.parent_id] = {
            ...parentTask,
            sub_tasks: updatedSubTasks,
          };
        }
      } else {
        // Handle main task update
        // Update the task attributes
        newDashboardTasksById[taskIdToUpdate] = {
          ...newDashboardTasksById[taskIdToUpdate],
          ...updatedAttributes,
        };

        // If status changed, move the task in dashboardTaskOrder
        if (newStatus && newStatus !== originalTask?.status) {
          // Remove from old status order
          newDashboardTaskOrder[originalTask?.status] = (
            newDashboardTaskOrder[originalTask?.status] || []
          ).filter(id => id !== taskIdToUpdate);

          // Add to new status order
          newDashboardTaskOrder[newStatus] = [
            taskIdToUpdate,
            ...(newDashboardTaskOrder[newStatus] || []),
          ];
        }
      }

      return {
        ...state,
        updateDashboardTaskLoading: true,
        updateDashboardTaskError: null,
        dashboardTasksById: newDashboardTasksById,
        dashboardTaskOrder: newDashboardTaskOrder,
      };
    }
    case types.UPDATE_DASHBOARD_TASK_SUCCESS:
      return {
        ...state,
        updateDashboardTaskLoading: false,
        updateDashboardTaskError: null,
      };
    case types.UPDATE_DASHBOARD_TASK_FAILURE: {
      const { originalTask } = payload;

      if (originalTask && originalTask.parent_id) {
        // This was a subtask, revert it within its parent task
        const parentTask = state.dashboardTasksById[originalTask.parent_id];
        if (parentTask) {
          const revertedSubTasks = parentTask.sub_tasks.map((subTask: Task) =>
            subTask.id === originalTask.id ? originalTask : subTask
          );
          return {
            ...state,
            updateDashboardTaskLoading: false,
            updateDashboardTaskError: payload,
            dashboardTasksById: {
              ...state.dashboardTasksById,
              [originalTask.parent_id]: {
                ...parentTask,
                sub_tasks: revertedSubTasks,
              },
            },
          };
        }
      }
      // For main tasks or if parent task not found
      return {
        ...state,
        updateDashboardTaskLoading: false,
        updateDashboardTaskError: payload,
        dashboardTasksById: originalTask
          ? {
              ...state.dashboardTasksById,
              [originalTask.id]: originalTask, // Restore original task
            }
          : state.dashboardTasksById,
      };
    }

    // Delete Task
    case types.DELETE_DASHBOARD_TASK_LOADING: {
      const { taskId, originalTask, parent_id } = payload;
      const newTasksAfterDelete = { ...state.dashboardTasksById };
      const updatedDashboardTaskOrder = { ...state.dashboardTaskOrder };

      if (parent_id) {
        // If it's a subtask, remove it from the parent's sub_tasks array
        const parentTask = newTasksAfterDelete[parent_id];
        if (parentTask) {
          const updatedSubTasks = (parentTask.sub_tasks || []).filter(
            (subTask: Task) => subTask.id !== taskId
          );
          newTasksAfterDelete[parent_id] = {
            ...parentTask,
            sub_tasks: updatedSubTasks,
          };
        }
      } else {
        // If it's a top-level task, remove it from dashboardTasksById and dashboardTaskOrder
        if (originalTask && originalTask.status) {
          updatedDashboardTaskOrder[originalTask.status] = (
            updatedDashboardTaskOrder[originalTask.status] || []
          ).filter(id => id !== taskId);
        }
        delete newTasksAfterDelete[taskId];
      }

      return {
        ...state,
        deleteDashboardTaskLoading: true,
        deleteDashboardTaskError: null,
        dashboardTasksById: newTasksAfterDelete,
        dashboardTaskOrder: updatedDashboardTaskOrder,
        originalTaskBeforeDelete: originalTask, // Store original for rollback
      };
    }
    case types.DELETE_DASHBOARD_TASK_SUCCESS:
      return {
        ...state,
        deleteDashboardTaskLoading: false,
        deleteDashboardTaskError: null,
        originalTaskBeforeDelete: null, // Clear stored original task
      };
    case types.DELETE_DASHBOARD_TASK_FAILURE: {
      const { taskId, originalTask, parent_id } = payload;
      const tasksAfterDeleteFailure = { ...state.dashboardTasksById };
      const updatedDashboardTaskOrder = { ...state.dashboardTaskOrder };

      if (originalTask) {
        if (parent_id) {
          // If it was a subtask, restore it to its parent's sub_tasks array
          const parentTask = tasksAfterDeleteFailure[parent_id];
          if (parentTask) {
            const updatedSubTasks = [
              ...(parentTask.sub_tasks || []),
              originalTask,
            ];
            tasksAfterDeleteFailure[parent_id] = {
              ...parentTask,
              sub_tasks: updatedSubTasks,
            };
          }
        } else {
          // If it was a top-level task, restore it to dashboardTasksById and dashboardTaskOrder
          tasksAfterDeleteFailure[taskId] = originalTask;
          if (originalTask.status) {
            updatedDashboardTaskOrder[originalTask.status] = [
              originalTask.id,
              ...(updatedDashboardTaskOrder[originalTask.status] || []),
            ];
          }
        }
      }

      return {
        ...state,
        deleteDashboardTaskLoading: false,
        deleteDashboardTaskError: payload.message,
        dashboardTasksById: tasksAfterDeleteFailure,
        dashboardTaskOrder: updatedDashboardTaskOrder,
        originalTaskBeforeDelete: null, // Clear stored original task
      };
    }

    // Get Task By ID
    case types.GET_DASHBOARD_TASK_BY_ID_LOADING:
      return {
        ...state,
        getDashboardTaskByIdLoading: true,
        getDashboardTaskByIdError: null,
      };
    case types.GET_DASHBOARD_TASK_BY_ID_SUCCESS:
      const fetchedTask = payload as Task;
      return {
        ...state,
        getDashboardTaskByIdLoading: false,
        getDashboardTaskByIdError: null,
        dashboardTasksById: {
          ...state.dashboardTasksById,
          [fetchedTask.id]: fetchedTask,
        },
      };
    case types.GET_DASHBOARD_TASK_BY_ID_FAILURE:
      return {
        ...state,
        getDashboardTaskByIdLoading: false,
        getDashboardTaskByIdError: payload,
      };

    // Table Column Preferences
    case types.LOAD_TABLE_COLUMN_PREFERENCES_LOADING:
      return {
        ...state,
        loadTableColumnPreferencesLoading: true,
        loadTableColumnPreferencesError: null,
      };
    case types.LOAD_TABLE_COLUMN_PREFERENCES_SUCCESS:
      return {
        ...state,
        loadTableColumnPreferencesLoading: false,
        loadTableColumnPreferencesError: null,
        tableColumnPreferences: payload as TableColumnPreference[],
      };
    case types.LOAD_TABLE_COLUMN_PREFERENCES_FAILURE:
      return {
        ...state,
        loadTableColumnPreferencesLoading: false,
        loadTableColumnPreferencesError: payload,
      };

    case types.UPDATE_TABLE_COLUMN_PREFERENCES_LOADING:
      return {
        ...state,
        updateTableColumnPreferencesLoading: true,
        updateTableColumnPreferencesError: null,
        tableColumnPreferences: payload as TableColumnPreference[], // Optimistically update with new preferences
      };
    case types.UPDATE_TABLE_COLUMN_PREFERENCES_SUCCESS:
      return {
        ...state,
        updateTableColumnPreferencesLoading: false,
        updateTableColumnPreferencesError: null,
        // State already updated optimistically, no change needed here
      };
    case types.UPDATE_TABLE_COLUMN_PREFERENCES_FAILURE:
      return {
        ...state,
        updateTableColumnPreferencesLoading: false,
        updateTableColumnPreferencesError: payload.message, // Assuming message is part of payload
        tableColumnPreferences: payload.originalPreferences as TableColumnPreference[], // Revert to original preferences
      };

    // View Mode (List/Kanban)
    case types.SET_DASHBOARD_VIEW_MODE:
      return { ...state, dashboardViewMode: payload as 'list' | 'kanban' };

    case types.SET_ALL_TASK_SELECTED: {
      const { checked, status } = payload as {
        checked: boolean;
        status: string;
      };
      console.log({ checked, status });
      const updatedTasksById = { ...state.dashboardTasksById };

      Object.keys(updatedTasksById).forEach(taskId => {
        const task = updatedTasksById[taskId];
        if (task.status === status) {
          // Update parent task's isSelected
          updatedTasksById[taskId] = {
            ...task,
            isSelected: checked,
          };

          // If it's a parent task with subtasks, update subtasks' isSelected
          if (task.sub_tasks && task.sub_tasks.length > 0) {
            const updatedSubTasks = task.sub_tasks.map(subtask => ({
              ...subtask,
              isSelected: checked,
            }));
            updatedTasksById[taskId] = {
              ...updatedTasksById[taskId], // Use the already updated parent task
              sub_tasks: updatedSubTasks,
            };
          }
        }
      });

      return {
        ...state,
        isAllTaskSelected: {
          ...state.isAllTaskSelected,
          [status]: checked,
        },
        dashboardTasksById: updatedTasksById,
      };
    }

    case types.CLEAR_ALL_TASK_SELECTION:
      const clearedTasks = Object.keys(state.dashboardTasksById).reduce(
        (acc, taskId) => {
          const task = state.dashboardTasksById[taskId];
          const updatedTask = {
            ...task,
            isSelected: false,
          };

          // Also deselect subtasks if they exist
          if (updatedTask.sub_tasks && updatedTask.sub_tasks.length > 0) {
            updatedTask.sub_tasks = updatedTask.sub_tasks.map(subtask => ({
              ...subtask,
              isSelected: false,
            }));
          }
          acc[taskId] = updatedTask;
          return acc;
        },
        {} as { [key: string]: Task }
      );
      return {
        ...state,
        isAllTaskSelected: {
          todo: false,
          in_progress: false,
          complete: false,
        },
        dashboardTasksById: clearedTasks,
      };

    case types.SET_SINGLE_TASK_SELECTED: {
      const { taskId, checked } = payload as {
        taskId: string;
        checked: boolean;
      };

      const updatedTasksById = { ...state.dashboardTasksById };
      // First, check if the taskId directly corresponds to a top-level task
      if (
        updatedTasksById[taskId] &&
        updatedTasksById[taskId].parent_id === null
      ) {
        updatedTasksById[taskId] = {
          ...updatedTasksById[taskId],
          isSelected: checked,
        };
      } else {
        // If not a top-level task, it must be a subtask. Iterate through parent tasks to find it.
        for (const parentTaskId in updatedTasksById) {
          const parentTask = updatedTasksById[parentTaskId];
          if (parentTask.sub_tasks && parentTask.sub_tasks.length > 0) {
            const updatedSubTasks = parentTask.sub_tasks.map((subTask: Task) =>
              subTask.id === taskId
                ? { ...subTask, isSelected: checked }
                : subTask
            );

            // If subtasks array changed, update the parent task immutably
            if (
              JSON.stringify(updatedSubTasks) !==
              JSON.stringify(parentTask.sub_tasks)
            ) {
              updatedTasksById[parentTaskId] = {
                ...parentTask,
                sub_tasks: updatedSubTasks,
              };
              break; // Subtask found and updated, exit loop
            }
          }
        }
      }

      return {
        ...state,
        dashboardTasksById: updatedTasksById,
      };
    }

    case types.RESET_DASHBOARD_TASKS:
      return {
        ...state,
        dashboardTasksById: {},
        dashboardTaskOrder: {
          todo: [],
          in_progress: [],
          complete: [],
        },
        dashboardTasksLoading: false, // Reset loading state as well
        dashboardTasksError: null, // Clear any previous errors
      };

    default:
      return state;
  }
};
